/**
 * The upload state machine, and nothing that touches a DOM.
 *
 * It is separated from the panel for the reason the settings model is: the
 * decisions here — how a file is cut into parts, where a resume starts from,
 * what a 409 means — are the ones worth testing without a browser, and they are
 * the ones a future reader will want to check without reading JSX.
 *
 * # The rule that shapes all of it
 *
 * **The server's offset wins.** A client that resumes from its own count of what
 * it sent is a client that duplicates a chunk it thought had failed and had not.
 * The host is where the bytes are, so the host is the only thing that can answer
 * how far an upload got; every start and every resume asks it first, and a
 * refusal carries the true offset so the answer to "we disagree" is always "use
 * theirs".
 */

/** How big one part may be. Mirrors hostfiles.MaxChunkBytes, which is itself set
 *  from nginx's body cap — a larger part is refused by the proxy before the
 *  server sees it, so this is not a number to tune for taste. */
export const MAX_CHUNK_BYTES = 32 * 1024 * 1024

/** One part of a file: where it starts and where it ends. */
export interface Chunk {
  start: number
  end: number
}

/**
 * Cuts a file into parts.
 *
 * An empty file yields one empty chunk rather than none, because "upload
 * nothing" still has to create the file: a zero-length log is a real thing to
 * put on a machine, and a plan with no chunks would finish without ever asking
 * the host for one.
 */
export function planChunks(size: number, max: number = MAX_CHUNK_BYTES): Chunk[] {
  if (size <= 0) return [{ start: 0, end: 0 }]
  const out: Chunk[] = []
  for (let start = 0; start < size; start += max) {
    out.push({ start, end: Math.min(start + max, size) })
  }
  return out
}

/**
 * Which chunks are still owed, given what the host says it already holds.
 *
 * Takes the server's offset rather than a count of what was sent. A chunk that
 * straddles the offset is re-sent from the offset rather than from its own
 * start, so a transfer interrupted half way through a part resumes at the byte
 * it stopped on and not at the beginning of a thirty-two megabyte block.
 */
export function remainingChunks(size: number, have: number, max: number = MAX_CHUNK_BYTES): Chunk[] {
  if (have >= size && size > 0) return []
  const from = Math.max(0, have)
  const out: Chunk[] = []
  for (let start = from; start < size; start += max) {
    out.push({ start, end: Math.min(start + max, size) })
  }
  if (out.length === 0) out.push({ start: from, end: from })
  return out
}

/**
 * `skipped` is not a flavour of `cancelled`, and keeping them apart is the
 * point. A cancel is the operator withdrawing work; a skip is them deciding the
 * file already on the host is the one they want. Answering "atla, kalan dosyalar
 * için de aynısını yap" over a fifty-eight file folder under one label would
 * report fifty-eight withdrawals, which is not what happened — and the row is a
 * receipt, so it has to say the true thing.
 */
export type TransferState =
  | 'queued'
  | 'hashing'
  | 'sending'
  | 'finishing'
  | 'done'
  | 'failed'
  | 'cancelled'
  | 'skipped'

/** Which way the bytes are going. A queue that does not say is a queue an
 *  operator has to reason about. */
export type Direction = 'up' | 'down'

/** One file in flight, either way. */
export interface Transfer {
  /** Stable across a resume, so a retry updates a row rather than adding one. */
  id: string
  direction: Direction
  rootId: string
  /** The path relative to the root, which is what the API names. */
  path: string
  name: string
  size: number
  sent: number
  state: TransferState
  /** Epoch milliseconds. Both are needed for a rate: one point cannot have a
   *  slope, and a rate measured from the moment the row was created would count
   *  the hashing pass as transfer time and report half the real speed. */
  startedAt?: number
  updatedAt?: number
  /** The server's own words when it refused. Never composed here: a message
   *  about a failure is the server's to write, and inventing one would tell an
   *  operator something nobody measured. */
  error?: string
}

/**
 * Bytes per second, or null when there is nothing to divide by yet.
 *
 * Null rather than zero, and rendered as an em dash rather than as "0 B/s": a
 * transfer running for four milliseconds has not been measured, and a confident
 * zero beside a progress bar that is visibly moving is the kind of invented
 * measurement this console refuses everywhere else.
 */
export function rateOf(t: Transfer, now: number = Date.now()): number | null {
  if (!t.startedAt || t.sent <= 0) return null
  const end = t.state === 'done' ? (t.updatedAt ?? now) : now
  const elapsed = (end - t.startedAt) / 1000
  if (elapsed < 0.5) return null
  return t.sent / elapsed
}

/** Seconds remaining at the current rate, or null when that cannot be known —
 *  an unmeasured rate, an empty file, or a transfer that is not running. */
export function etaOf(t: Transfer, now: number = Date.now()): number | null {
  if (t.state !== 'sending') return null
  const rate = rateOf(t, now)
  if (!rate || t.size <= 0) return null
  const left = t.size - t.sent
  return left <= 0 ? null : left / rate
}

export type TransferEvent =
  | { kind: 'queued'; transfer: Transfer }
  | { kind: 'hashing'; id: string }
  /** The host's offset, which always wins over what the client believed. */
  | { kind: 'resumed'; id: string; have: number }
  | { kind: 'progress'; id: string; sent: number }
  | { kind: 'finishing'; id: string }
  | { kind: 'done'; id: string }
  | { kind: 'failed'; id: string; error: string }
  | { kind: 'cancelled'; id: string }
  | { kind: 'skipped'; id: string }
  | { kind: 'cleared' }

/** Every event may carry the moment it happened, so a test can drive the clock
 *  rather than sleep. */
export type TimedEvent = TransferEvent & { at?: number }

/**
 * The list, reduced.
 *
 * A finished transfer stays in the list. It is the receipt: an operator who has
 * just put a binary on SRV5 wants to see that it landed, and a row that vanishes
 * on success leaves them wondering whether it ever started. Clearing is a thing
 * they ask for.
 */
export function reduceTransfers(list: Transfer[], event: TimedEvent): Transfer[] {
  if (event.kind === 'cleared') {
    return list.filter(
      (t) =>
        t.state !== 'done' &&
        t.state !== 'cancelled' &&
        t.state !== 'skipped' &&
        t.state !== 'failed',
    )
  }
  if (event.kind === 'queued') {
    // Replacing rather than appending, so a retry of the same destination
    // updates its row. Two rows for one file is how a transfer list becomes a
    // log nobody reads.
    const without = list.filter((t) => t.id !== event.transfer.id)
    return [...without, event.transfer]
  }
  const now = event.at ?? Date.now()
  return list.map((t) => {
    if (t.id !== event.id) return t
    switch (event.kind) {
      case 'hashing':
        return { ...t, state: 'hashing' as const }
      case 'resumed':
        // The server's number, not an increment. This is the whole point. The
        // clock starts here rather than at `queued`, so the hashing pass does
        // not count against the rate.
        return { ...t, state: 'sending' as const, sent: event.have, startedAt: now, updatedAt: now }
      case 'progress':
        return { ...t, state: 'sending' as const, sent: event.sent, updatedAt: now }
      case 'finishing':
        return { ...t, state: 'finishing' as const, sent: t.size, updatedAt: now }
      case 'done':
        return { ...t, state: 'done' as const, sent: t.size, updatedAt: now, error: undefined }
      case 'failed':
        return { ...t, state: 'failed' as const, error: event.error }
      case 'cancelled':
        return { ...t, state: 'cancelled' as const }
      case 'skipped':
        return { ...t, state: 'skipped' as const }
      default:
        return t
    }
  })
}

/** A transfer that is still going, for the "n in flight" count and for warning
 *  before navigating away. */
export function isActive(t: Transfer): boolean {
  return t.state === 'queued' || t.state === 'hashing' || t.state === 'sending' || t.state === 'finishing'
}

/**
 * The SHA-256 the server verifies against, computed over the whole file before
 * the first byte is sent.
 *
 * Before, not during: the digest is what the host compares the accumulated part
 * against, and computing it as the chunks go would mean hashing what was read
 * rather than what is on disk. A file edited mid-upload would then pass a check
 * of its own corruption.
 *
 * Requires a secure context, which the console is (`crypto.subtle` is undefined
 * over plain http). A deployment served without TLS gets a stated failure rather
 * than a silent one.
 */
export async function fileDigest(file: Blob): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('crypto.subtle is unavailable; a secure context is required to verify an upload')
  }
  const buf = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buf)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Joins a directory and a name into the relative path the API takes.
 *
 * Deliberately not `path.join`: a leading slash means "absolute" to the server
 * and is refused there, and a `..` is refused there too. This composes and the
 * server validates — the same division the Go side draws between validating and
 * sanitising.
 */
export function relPath(dir: string, name: string): string {
  const d = dir.replace(/^\/+|\/+$/g, '')
  return d === '' ? name : `${d}/${name}`
}

/**
 * The bounded pool, and the rule that decides what may start.
 *
 * ADR-0026 §11 ran transfers one at a time, and its reason was right: every file
 * is an SSH exec on the far host, and the per-file selection that fired two
 * hundred at once was a fork bomb aimed at a machine the fleet is working on.
 * What was too strong was the conclusion that the only safe number is one.
 *
 * The ceiling comes from `sshd`'s `MaxStartups`, default `10:30:100`, and not
 * from `MaxSessions`: every request here dials afresh, so N parallel transfers
 * are N concurrent handshakes competing with the terminal wall's own. Past ten
 * in flight `sshd` refuses at 30% probability ramping to certainty, and a
 * refused dial arrives as an opaque 500. A non-deterministic, unexplained
 * failure is the worst thing a transfer queue can produce. ADR-0027 §5.
 */

/** Three workers, which is a starting point rather than a measurement — the
 *  control is in the interface so an operator can find out. */
export const DEFAULT_PARALLEL = 3

/** The ceiling, argued from `MaxStartups` above. */
export const MAX_PARALLEL = 6

/** What the control offers. One is kept because it is what ADR-0026 §11 shipped
 *  and because it is the setting to reach for when a host is already loaded. */
export const PARALLEL_CHOICES = [1, 3, 6] as const

/** Occupying a worker, as opposed to merely being in the list. Deliberately
 *  narrower than [isActive], which counts `queued` because it answers a
 *  different question — "is anything still going" for the leave-warning. A
 *  budget computed from [isActive] would shrink as the queue filled and the pool
 *  would never start a second file. */
function isRunning(t: Transfer): boolean {
  return t.state === 'hashing' || t.state === 'sending' || t.state === 'finishing'
}

/** How many transfers hold a worker right now. Every state that is not terminal
 *  and not still waiting. */
export function runningCount(list: Transfer[]): number {
  return list.filter(isRunning).length
}

/**
 * Which queued transfers may start now.
 *
 * # Why `started` is a parameter, and not something this could work out
 *
 * `Transfer[]` cannot express "started but not yet reported". Dispatching a
 * state change is asynchronous relative to the effect that admits: between
 * launching a job and its `hashing` event landing in the list, the row still
 * reads `queued` and `runningCount` cannot see it. So an admit run twice in that
 * window — a second render, another event, a `useEffect` that re-fires — hands
 * back the same id both times, and the file is uploaded twice.
 *
 * The caller therefore holds the set of ids it has already launched and passes
 * it in. Those ids are excluded from the answer, *and* the queued ones among
 * them are counted against the budget: they are the running transfers this list
 * cannot see yet, and ignoring them would let the pool open six sockets while
 * `runningCount` still read zero.
 *
 * The contract that makes this work is that the caller **removes an id from
 * `started` when its job settles** — done, failed or cancelled. A stale id in
 * the set holds a worker that nothing will ever release, so a requeued retry
 * would sit there forever.
 *
 * FIFO over the list, because the list is already the order the operator asked
 * for; a pool that reorders a folder makes the queue unreadable while it runs.
 */
export function admit(list: Transfer[], limit: number, started: ReadonlySet<string>): string[] {
  // Clamped here rather than at the setting, because this is the one place the
  // ceiling cannot be forgotten. The number reaches us from stored state, and a
  // stored number is not a trusted one — the `MaxStartups` argument holds
  // whatever it was edited to.
  //
  // Coerced and range-checked, not just `Math.min`, because `Math.min` is not a
  // clamp when its argument is not a number. `Math.min(NaN, 6)` is `NaN`, and
  // `NaN` compares false against the `>=` below, so every `break` is skipped and
  // the whole queue is admitted at once — the fork bomb this bound exists to
  // prevent, produced by the bound itself. A missing key hands back `undefined`
  // and a corrupted one an object, so this is the ordinary path for a setting
  // read from storage rather than a hypothetical.
  //
  // Unreadable falls back to 1, which is what ADR-0026 §11 shipped: it is the
  // one number that cannot be too many, and it keeps the queue moving. Zero
  // stays a pause, because zero is a number somebody asked for.
  const asked = Number(limit)
  const cap = Number.isFinite(asked) ? Math.min(Math.max(0, Math.trunc(asked)), MAX_PARALLEL) : 1
  const taken = list.filter((t) => isRunning(t) || (t.state === 'queued' && started.has(t.id))).length
  const budget = cap - taken
  if (budget <= 0) return []

  const out: string[] = []
  for (const t of list) {
    if (out.length >= budget) break
    if (t.state !== 'queued' || started.has(t.id)) continue
    out.push(t.id)
  }
  return out
}

/**
 * What the queue header can say about a batch.
 *
 * Over whatever list it is handed: this module does not know where one batch
 * ends and the next begins, and a finished row stays in the list as a receipt
 * until it is cleared.
 *
 * A cancelled row is left out of the byte totals entirely. It is work the
 * operator withdrew, and leaving its size in the denominator would park the bar
 * short of the end with nothing running — which reads as a stall, when what
 * happened is that the batch finished as asked. A *failed* row is counted,
 * because it is still owed: hiding it would show a complete-looking bar over an
 * incomplete transfer, which is the defect this console refuses everywhere else.
 */
export function batchTotals(list: Transfer[]): {
  done: number
  failed: number
  queued: number
  running: number
  sent: number
  bytes: number
} {
  let done = 0
  let failed = 0
  let queued = 0
  let sent = 0
  let bytes = 0
  for (const t of list) {
    if (t.state === 'done') done += 1
    else if (t.state === 'failed') failed += 1
    else if (t.state === 'queued') queued += 1
    if (t.state === 'cancelled' || t.state === 'skipped') continue
    sent += t.sent
    bytes += t.size
  }
  return { done, failed, queued, running: runningCount(list), sent, bytes }
}

/**
 * What the pool is moving, in bytes per second, or null when nothing has been
 * measured.
 *
 * The sum of the rows actually moving, rather than total bytes over wall time
 * since the batch began. Wall time is the wrong denominator here: it counts the
 * hashing passes and the gaps between a worker finishing and the next file
 * starting, so it answers "how much did this batch average" while the number
 * sits beside a bar that is moving now. Summing [rateOf] also means the header
 * and the rows are the same measurement, so they cannot disagree.
 *
 * Null and never zero, the discipline [rateOf] already states: a pool four
 * milliseconds into its first file has not been measured, and a confident zero
 * is an invented one.
 */
export function batchRateOf(list: Transfer[], now: number = Date.now()): number | null {
  let total = 0
  let measured = false
  for (const t of list) {
    if (t.state !== 'sending') continue
    const r = rateOf(t, now)
    if (r === null) continue
    total += r
    measured = true
  }
  return measured ? total : null
}

/**
 * Seconds until the pool has moved everything it is going to, or null when that
 * cannot be known.
 *
 * The remainder covers what will actually move — what is queued and what is
 * running. A failed row is owed but nothing is carrying it, so counting it would
 * produce an estimate that can never arrive; it is the retry that puts it back
 * in the queue and back in this number.
 */
export function batchEtaOf(list: Transfer[], now: number = Date.now()): number | null {
  const rate = batchRateOf(list, now)
  if (!rate) return null
  let left = 0
  for (const t of list) {
    if (t.state === 'queued' || isRunning(t)) left += Math.max(0, t.size - t.sent)
  }
  return left <= 0 ? null : left / rate
}
